import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import SatelliteDataReportHeader from "../../../components/SatelliteDataReport/SatelliteDataReportHeader";
import SatelliteDataReportFooter from "../../../components/SatelliteDataReport/SatelliteDataReportFooter";
import { useTranslation } from "react-i18next";
import SatelliteDataReportSubHeader from "../../../components/SatelliteDataReport/SatelliteDataReportSubHeader";
import api from "../../../globals/axios";
import { getEmojiFromColor } from "../../../globals/utils";

const SatelliteDataReportSummaryPage = ({ farmId, date, language }) => {
  const { t } = useTranslation();
  const [, setLoading] = useState(false);
  const [farmData, setFarmData] = useState(null);

  useEffect(() => {
    const fetchReportDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          "/farmer/get-farm-satellite-data-report-details/",
          {
            params: {
              farmId: farmId,
              date: date,
              ln: language,
            },
          }
        );
        setFarmData(response.data.data);
      } catch (err) {
        console.error(
          "Error fetching farm satellite data report details:",
          err
        );
      } finally {
        setLoading(false);
      }
    };

    if (farmId && date) {
      fetchReportDetails();
    }
  }, [farmId, date]);

  const basicMetrics = [
    { label: t("Farmer Name"), value: farmData?.farm_details.farmer_name },
    { label: t("Farm Name"), value: farmData?.farm_details.farm_name },
    { label: t("Assessment Date"), value: farmData?.farm_details.date },
    { label: t("Area (acres)"), value: farmData?.farm_details.farm_area },
    { label: t("Soil Texture"), value: farmData?.farm_details.soil_texture },
  ];

  const parameters = [
    {
      name: t("Overall Crop Growth"),
      text:
        farmData?.satellite_data_parameters.overall_crop_growth?.text || "N/A",
      color:
        farmData?.satellite_data_parameters.overall_crop_growth?.color || "red",
    },
    {
      name: t("Soil Moisture"),
      text: farmData?.satellite_data_parameters.soil_moisture?.text || "N/A",
      color: farmData?.satellite_data_parameters.soil_moisture?.color || "red",
    },
  ];

  return (
    <Box sx={{ maxWidth: 800, margin: "auto", backgroundColor: "#ffffff" }}>
      <SatelliteDataReportHeader />

      {/* Satellite Report Basic Details */}
      <Grid
        container
        spacing={1}
        sx={{
          mb: 3,
          p: 2,
          mt: 1,
        }}
      >
        {basicMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
              }}
            >
              <Grid container spacing={2}>
                <Grid
                  item
                  xs={6}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px !important",
                  }}
                >
                  <Typography sx={{ color: "#237D41" }}>
                    <strong>{metric.label}:</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={6}
                  my="auto"
                  sx={{
                    bgcolor: "#237D411A",
                    padding: "10px !important",
                  }}
                >
                  <Typography sx={{ color: "#237D41", my: "auto" }}>
                    {metric.value}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Satellite Report Metrics */}
      <Paper sx={{ mb: 1, borderRadius: 0, boxShadow: 0 }}>
        <SatelliteDataReportSubHeader text={t("Parameter")} />
        <Box sx={{ px: 2, py: 3, bgcolor: "#237D410D", borderRadius: 0 }}>
          {parameters.map((param, index) => {
            const { name, color, text } = param;
            const emoji = getEmojiFromColor(color);

            return (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "5px !important",
                      }}
                    >
                      <Typography sx={{ color: "#237D41" }}>{name}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      my="auto"
                      sx={{
                        bgcolor: "#FFFFFF",
                        padding: "5px 0 5px 15px !important",
                        display: "flex",
                        flexDirection: "row",
                        border: "0.2px solid #D9D9D9",
                      }}
                    >
                      <Typography sx={{ color: color, my: "auto" }}>
                        {emoji}
                      </Typography>
                      <Typography
                        sx={{
                          color: color,
                          my: "auto",
                          paddingLeft: "10px",
                        }}
                      >
                        {text}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            );
          })}
        </Box>
      </Paper>

      {/* Satellite Report Insights */}
      <Paper sx={{ borderRadius: 0, boxShadow: 0 }}>
        <SatelliteDataReportSubHeader text={t("Insights")} />

        <Box sx={{ px: 4, py: 2, bgcolor: "#237D410D" }}>
          <List sx={{ bgcolor: "#ffffff", boxShadow: "#C3BCBC40" }}>
            {farmData?.insights.map((insight, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ padding: "5px 10px" }}>
                  <ListItemText
                    primary={`${index + 1}. ${insight}`}
                    sx={{ color: "#237D41" }}
                  />
                </ListItem>
                {index < farmData.insights.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* Satellite Report Footer  */}
        <SatelliteDataReportFooter />
      </Paper>
    </Box>
  );
};

export default SatelliteDataReportSummaryPage;
